import { ReactElement, useEffect,  useState } from "react";
import { useSelector } from 'react-redux';
import { useNavigate } from "react-router-dom";
import { T } from "react-translator-component";
import { Box, Button, Checkbox, FormControlLabel, Typography } from "@mui/material";
import { colors } from "../../common/colors";
import LoadingModal from "../../components/LoadingModal";
import TwoButtonModal from "../../components/TwoButtonModal";
import { ErrorMessageModal } from "../../components/ErrorMessageModal";
import MessageModal from "../../components/Models/GreenCity/MessageModal";
import GCBackButton from "../../components/GreenCity/GCBackButton";
import Separator from "../../components/Separator";
import appServices from "../../services/app-services/appServices";
function GroupCloseDetails(): ReactElement {
  const defaultImage = require("../../assets/images/default.png");
  const navigate = useNavigate();

  const groupInfo = useSelector((state: any) => state.gcGroup);

  const groupCloseErrorMessage: string[] = [
    T('group_close_error_1'),
    '　',
    T('group_close_error_2'),
    T('group_close_error_3'),
    T('group_close_error_4'),
    T('group_close_error_5')
  ]

  // 初回実行
  useEffect(() => {
    checkIfRejectedReimbursementsExist(groupInfo)
  }, [groupInfo]);

  const checkIfRejectedReimbursementsExist = async (memberGroup: any) => {
    let size = 5
    try {
      const res = await appServices.getPaymentGroupRejectedReimbursements(memberGroup?.id)
      if (res.reimbursements.length === 0) {
        size = 4
      }
    } catch {
      // 取得できない場合は念のため拒否された立て替え精算があることにしておく
    }
    setCheckedItems(new Array(size).fill(false))
  }

  // クローズ処理
  const [isShowConfirmationModal, setIsShowConfirmationModal] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [isShowOkModal, setIsShowOkModal] = useState<boolean>(false);
  const [isShowErrorModal, setIsShowErrorModal] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string[]>([]);
  const handleSubmit = async () => {
    setLoading(true)
    setIsShowConfirmationModal(false)
    try {
      await appServices.closeMemberGroup(groupInfo?.id)
      setIsShowOkModal(true)
      return true
    } catch (error: any) {
      console.error(error)
      const errorKey = error?.response?.data?.error_key
      const responseMessage = error?.response?.data?.message ?? [T("api_unknown_error")]
      setErrorMessage(errorKey === "cannot_close" ? groupCloseErrorMessage : responseMessage)
      setIsShowErrorModal(true)
      return false
    } finally {
      setLoading(false)
    }
  }

  // クローズ完了
  const close = () => navigate("/myfarm")

  // チェックボックス
  const [checkedItems, setCheckedItems] = useState<boolean[]>([])
  const handleCheckbox = (i: number) => {
    if (checkedItems.length <= i) {
      return
    }
    const items = [ ... checkedItems ]
    items[i] = !items[i]
    setCheckedItems(items)
  }

  // 描画
  return (
    <div style={{ paddingTop: 100 }}>
      <LoadingModal visible={loading} handleClose={() => {}} />
      <GCBackButton onClick={() => navigate(-1)} />
      <Box component="div" style={style.topContainer}>
        <img alt="" height={26} width={26} style={{ borderRadius: 13, marginRight: 10 }}
          src={groupInfo?.groupImage instanceof File ? URL.createObjectURL(groupInfo.groupImage) : defaultImage}
        />
        &nbsp;  {groupInfo?.groupName}
      </Box>
      <Separator sx={{ marginTop: "10px" }} color="gray" />
      <Box component="div" sx={style.container}>
        <Typography style={style.description}>
          {T('group_close_details_description')}
        </Typography>
        {
          checkedItems.map((item: boolean, i: number) => (
            <FormControlLabel
              control={<Checkbox checked={item} onChange={(e) => handleCheckbox(i)} />}
              sx={style.checkboxController}
              label={T(`group_close_details_checkbox_${i + 1}`)}
              key={i}
            />
          ))
        }
        <Button
          disabled={!Object.values(checkedItems).every((v) => v)}
          color="secondary"
          onClick={() => setIsShowConfirmationModal(true)}
          sx={style.submitButton}
          size={"large"}
          variant="contained"
        >
          {T('close_group')}
        </Button>
      </Box>

      <TwoButtonModal
        open={isShowConfirmationModal}
        message={T('group_close_details_confirmation')[0]}
        onClose={() => setIsShowConfirmationModal(false)}
        onContinue={handleSubmit}
        noText={T('back_kanji')}
      />

      {/* クローズ成功 */}
      <MessageModal
        open={isShowOkModal}
        onClose={() => close()}
        onContinue={() => {}}
        message={T('group_close_details_modal_description')}
        showButton
        buttonText={T("close")}
        buttonStyles={style.dialogButton}
      />

      {/* クローズ失敗 */}
      <ErrorMessageModal
        open={isShowErrorModal}
        messages={errorMessage}
        onClose={() => setIsShowErrorModal(false)}
        alignLeft={errorMessage.length > 1}
      />
    </div>
  )
}

export default GroupCloseDetails

const style = {
  topContainer: {
    display: "flex",
    marginTop: 10,
    padding: "0 10px",
    fontWeight: "700"
  },
  avatar: {
    height: 26,
    width: 26,
    marginRight: "10px"
  },

  container: {
    padding: "6px",
    textAlign: "center"
  },
  description: {
    fontWeight: "700",
    margin: "15px 15px 65px 15px"
  },
  checkboxController: {
    display: "flex",
    alignItems: "flex-start",
    "& .MuiCheckbox-root": {
      paddingTop: 0
    },
    "& .MuiFormControlLabel-label": {
      textAlign: "left",
      paddingBottom: "20px"
    }
  },
  submitButton: {
    width: "80%",
    alignSelf: "center",
    margin: "50px auto 10px auto",
    display: "flex",
    borderRadius: 20,
    border: "2px solid",
    marginBottom: 8,
    boxShadow: "none",
    "&:hover": {
      boxShadow: "none",
    },
  },
  modalContainer: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "85%",
    maxWidth: "320px",
    padding: "50px 10px 20px 10px",
    bgcolor: "background.paper",
    boxShadow: 24,
    borderRadius: "10px",
    transition: "opacity 0.3s ease-in-out",
    textAlign: "center"
  },
  modalButtonContainer: {
    alignItems: "center",
    justifyContent: "center",
    marginTop: "30px"
  },
  dialogButton: {
    width: "50%",
    marginTop: "30px",
    minWitdh: "100px",
    alignSelf: "center",
    display: "inline-block",
    border: "3px solid",
    borderRadius: "10px",
    borderColor: colors.farm_blue200,
    color: colors.farm_black
  }
};
